import { useMemo } from 'react';
import type { OnboardingFlow, OnboardingTask } from '../types';
import { groupRequiredHotspotKeysByGroup, isTaskCompleted } from '../utils';

type UseOnboardingTaskProgressParams = {
  flow: OnboardingFlow;
  completedHotspots: Set<string>;
  completedGroups?: string[];
};

export type OnboardingTaskWithProgress = OnboardingTask & {
  isCompleted: boolean;
};

export function useOnboardingTaskProgress({
  flow,
  completedHotspots,
  completedGroups = [],
}: UseOnboardingTaskProgressParams) {
  return useMemo(() => {
    const completedGroupSet = new Set(completedGroups);
    const requiredHotspotKeysByGroup = groupRequiredHotspotKeysByGroup(flow.hotspots);

    const tasks = flow.tasks.map<OnboardingTaskWithProgress>((task) => {
      const requiredHotspotKeys = requiredHotspotKeysByGroup[task.id] ?? [];

      return {
        ...task,
        isCompleted: isTaskCompleted({
          taskId: task.id,
          requiredHotspotKeys,
          completedHotspots,
          completedGroupSet,
        }),
      };
    });

    const completedCount = tasks.filter((task) => task.isCompleted).length;
    const totalCount = tasks.length;
    const pendingCount = totalCount - completedCount;
    const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
    const isAllCompleted = totalCount > 0 && completedCount === totalCount;

    return {
      tasks,
      completedCount,
      totalCount,
      pendingCount,
      progressPercent,
      isAllCompleted,
    };
  }, [flow.tasks, flow.hotspots, completedHotspots, completedGroups]);
}
